import React, { useCallback, useState } from 'react'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { IError } from 'types/FMSGraphqlTypes'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { TEST_IDS } from '../../constants'
import { useDeclineOfferMutation } from './graphql/DeclineOfferMutation'

const MODAL_TITLE = 'Are you sure you want to decline this offer?'
const TOAST_SUCCESS_MESSAGE = 'Offer has been successfully declined.'

export interface IDeclineShipmentOfferConfirmationModalProps
  extends Pick<IConfirmationModalProps, 'onClose'> {
  externalId: string
  onDeclineSuccess: () => void
}

const DeclineShipmentOfferConfirmationModal = ({
  externalId,
  onClose,
  onDeclineSuccess,
}: IDeclineShipmentOfferConfirmationModalProps) => {
  const [mutationErrors, setMutationErrors] = useState<IError[]>([])

  const { trackEvent } = useFMSAnalytics()
  const { openToast } = useGlobalToast()

  const { declineShipmentOfferConfirmationModal } = TEST_IDS
  const { cancelButton, container, declineOfferButton } = declineShipmentOfferConfirmationModal

  const [declineOffer, { error, loading }] = useDeclineOfferMutation()

  const declineOfferHandler = useCallback(async () => {
    setMutationErrors([])
    const { data } = await declineOffer({
      variables: {
        input: {
          marketplaceId: externalId,
        },
      },
    })

    const { declineShipment } = data || {}
    const { shipment, errors = [] } = declineShipment || {}
    const { id: shipmentId } = shipment || {}

    if (errors.length) {
      trackEvent('Offers', 'FAILED_TO_DECLINE_OFFER', { errors })
      setMutationErrors(errors)
    }

    if (shipmentId) {
      trackEvent('Offers', 'SUCCESSFULLY_DECLINED_OFFER', { shipmentId })
      openToast({ toastMessage: TOAST_SUCCESS_MESSAGE, toastType: 'default' })
      onDeclineSuccess()
    }
  }, [declineOffer, externalId, onDeclineSuccess, openToast, trackEvent])

  const modalActions: IConfirmationModalProps['actions'] = [
    {
      label: 'Cancel',
      type: 'dismiss',
      disabled: loading,
      action: onClose,
      dataTest: cancelButton,
    },
    {
      label: 'Yes, Decline Offer',
      type: 'confirm',
      disabled: loading,
      action: declineOfferHandler,
      dataTest: declineOfferButton,
    },
  ]

  return (
    <ConfirmationModal
      title={MODAL_TITLE}
      dataTest={container}
      actions={modalActions}
      onClose={onClose}
      size={3}
      open>
      <ApolloErrorMessages mutationErrors={mutationErrors} apolloErrors={error} />
    </ConfirmationModal>
  )
}

export default DeclineShipmentOfferConfirmationModal
